import { Database, FlaskConical, Brain, AlertTriangle, Layers3, CheckCircle2 } from "lucide-react";

const Chip = ({ children, tone = "slate", testId }) => {
  const tones = {
    slate: "bg-white/5 border-white/10 text-slate-300",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    red: "bg-red-500/10 border-red-500/30 text-red-300",
    cyan: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
  };
  return <span data-testid={testId} className={`text-[11px] font-mono-num border rounded-full px-2 py-0.5 ${tones[tone]}`}>{children}</span>;
};

export const SignalEvidence = ({ result }) => {
  const p = result?.probability;
  const dq = result?.data_quality;
  const mtf = result?.mtf;
  const ind = result?.computed_indicators;
  if (!p && !dq) return null;
  const backtested = !!p?.backtested;
  const edgeTone = p?.edge === "positive" ? "emerald" : p?.edge === "weak" ? "amber" : "red";

  return (
    <div className="glass rounded-2xl p-5 space-y-4" data-testid="signal-evidence">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${backtested ? "bg-emerald-500/15" : "bg-amber-500/15"}`}>
          {backtested ? <FlaskConical className="w-5 h-5 text-emerald-400" /> : <Brain className="w-5 h-5 text-amber-400" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-slate-400">Probability source</p>
          <p className="font-semibold text-sm" data-testid="probability-label">{p?.label || "AI confidence estimate"}</p>
          <p className="text-sm text-slate-300 mt-0.5" data-testid="probability-display">{p?.display}</p>
          {backtested && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {p.patterns?.map((x, i) => <Chip key={i}>{x}</Chip>)}
              <Chip tone="cyan">target +{p.target_atr} ATR · stop −{p.stop_atr} ATR · {p.horizon_bars} bars</Chip>
              {p.expectancy_r != null && <Chip tone={edgeTone} testId="probability-edge">{p.edge} edge · {p.expectancy_r > 0 ? "+" : ""}{p.expectancy_r}R / trade</Chip>}
            </div>
          )}
          {p?.warning && (
            <p className="flex items-start gap-1.5 text-xs text-amber-300 mt-2" data-testid="probability-warning"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{p.warning}</p>
          )}
        </div>
      </div>

      {mtf && (
        <div className={`rounded-lg border px-3 py-2 text-sm flex items-start gap-2 ${mtf.against ? "border-red-500/30 bg-red-500/10" : "border-white/10 bg-white/5"}`} data-testid="mtf-check">
          <Layers3 className={`w-4 h-4 shrink-0 mt-0.5 ${mtf.against ? "text-red-400" : mtf.aligned ? "text-emerald-400" : "text-slate-400"}`} />
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">Higher timeframe · {mtf.timeframe}</p>
            <p className={mtf.against ? "text-red-300" : "text-slate-200"}>
              Trend <span className="capitalize font-semibold">{mtf.trend}</span>{mtf.aligned && <span className="text-emerald-400"> · aligned with signal</span>}
            </p>
            {mtf.warning && <p className="text-xs text-red-300 mt-0.5" data-testid="mtf-warning">{mtf.warning}</p>}
          </div>
        </div>
      )}

      {dq && (
        <div data-testid="data-quality">
          <div className="flex items-center gap-2 flex-wrap">
            <Database className="w-4 h-4 text-cyan-400" />
            <p className="text-xs uppercase tracking-wider text-slate-400">Data</p>
            {dq.available ? (
              <>
                <Chip tone="emerald" testId="data-provider">{dq.provider} · {dq.symbol}</Chip>
                <Chip>{dq.bars?.toLocaleString()} × {p?.timeframe || dq.timeframe} candles · {dq.span}</Chip>
                <Chip>live close {dq.last_close}</Chip>
                {dq.llm_calls === 2 && <Chip tone="cyan">2-pass (auto-detected)</Chip>}
              </>
            ) : <Chip tone="amber" testId="data-missing">screenshot OCR only</Chip>}
          </div>
          {dq.warnings?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {dq.warnings.map((w, i) => <li key={i} className="flex items-start gap-1.5 text-xs text-amber-300" data-testid={`data-warning-${i}`}><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}</li>)}
            </ul>
          )}
        </div>
      )}

      {ind && (
        <div data-testid="computed-indicators">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Indicators computed in code ({ind.timeframe})</p>
          <div className="flex flex-wrap gap-1.5">
            <Chip tone={ind.rsi_state === "oversold" ? "emerald" : ind.rsi_state === "overbought" ? "red" : "slate"}>RSI {ind.rsi}</Chip>
            <Chip tone={String(ind.macd_state).startsWith("bullish") ? "emerald" : "red"}>MACD {ind.macd_hist > 0 ? "+" : ""}{ind.macd_hist}</Chip>
            <Chip>EMA20 {ind.ema20}</Chip><Chip>EMA50 {ind.ema50}</Chip>
            {ind.ema200 != null && <Chip>EMA200 {ind.ema200}</Chip>}
            <Chip>ATR {ind.atr} ({ind.atr_pct}%)</Chip>
            <Chip tone={ind.volume_trend === "rising" ? "cyan" : "slate"}>Vol {ind.volume_ratio}x · {ind.volume_trend}</Chip>
            <Chip tone={ind.trend === "bullish" ? "emerald" : ind.trend === "bearish" ? "red" : "amber"}>trend {ind.trend}</Chip>
          </div>
        </div>
      )}
    </div>
  );
};
